import { useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface Lite {
  n: string;
  k: string;
  img?: string;
}

interface HistoryRow {
  a: Lite;
  b: Lite;
  av: number;
  bv: number;
  pick: number;
  right: boolean;
}

interface View {
  rounds: number;
  winAt: number;
  right: number;
  history: HistoryRow[];
  current?: { a: Lite; b: Lite };
}

interface Reveal {
  pair: { a: Lite; b: Lite };
  av: number;
  bv: number;
  pick: number;
  right: boolean;
}

const COPY = { bad_pick: "Тапни одну з карток" };

const KIND: Record<string, string> = { p: "🕵️ людина", f: "🎬 фільм", c: "🏙 місто", s: "🌍 країна" };

function fmt(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(".", ",")} млн`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} тис`;
  return `${v}`;
}

function Card({
  e,
  onPick,
  disabled,
  revealed,
}: {
  e: Lite;
  onPick?: () => void;
  disabled: boolean;
  revealed?: { v: number; winner: boolean; mine: boolean };
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled || !onPick}
      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors ${
        revealed
          ? revealed.winner
            ? "bg-ok/25"
            : "bg-cold/20"
          : "bg-tgsection active:bg-tgbutton/20"
      }`}
    >
      {e.img ? (
        <img src={e.img} alt="" draggable={false} className="h-20 w-20 shrink-0 rounded-xl object-cover" />
      ) : (
        <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-[rgba(128,128,128,0.15)] text-3xl">
          {KIND[e.k]?.split(" ")[0] ?? "❓"}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] text-tghint">{KIND[e.k] ?? ""}</span>
        <span className="mt-0.5 block text-lg font-extrabold leading-tight">{e.n}</span>
        {revealed ? (
          <span className="mt-1 block text-sm font-semibold tabular-nums">
            {fmt(revealed.v)} 👁{revealed.mine ? " · твій вибір" : ""}
          </span>
        ) : (
          <span className="mt-1 block text-xs text-tghint">тапни, якщо це читають більше</span>
        )}
      </span>
    </button>
  );
}

export function Khaip({ date }: { date?: string | undefined }) {
  const g = useGame<View>("khaip", COPY, date);
  const [reveal, setReveal] = useState<Reveal | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const pick = async (p: 0 | 1) => {
    if (g.busy || reveal || !state?.view.current) return;
    const pair = state.view.current;
    const next = await g.run(() => api.guess<View>("khaip", String(p), date));
    if (next) {
      const extra = next.extra as { right: boolean; av: number; bv: number } | undefined;
      if (extra) {
        if (!extra.right) haptic.error();
        setReveal({ pair, av: extra.av, bv: extra.bv, pick: p, right: extra.right });
        window.setTimeout(() => setReveal(null), 1500);
      }
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const shown = reveal
    ? { a: reveal.pair.a, b: reveal.pair.b }
    : v.current ?? null;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ХАЙП" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        <div className="mt-1 text-center text-xs text-tghint">
          що читають на Вікіпедії більше? · {v.right}/{v.history.length} влучно · раунд{" "}
          {Math.min(v.history.length + (finished ? 0 : 1), v.rounds)}/{v.rounds}
        </div>

        {shown && (
          <div className="mt-3 space-y-2">
            <Card
              e={shown.a}
              onPick={() => void pick(0)}
              disabled={g.busy || Boolean(reveal)}
              {...(reveal
                ? { revealed: { v: reveal.av, winner: reveal.av > reveal.bv, mine: reveal.pick === 0 } }
                : {})}
            />
            <div className="text-center text-xs font-bold text-tghint">ПРОТИ</div>
            <Card
              e={shown.b}
              onPick={() => void pick(1)}
              disabled={g.busy || Boolean(reveal)}
              {...(reveal
                ? { revealed: { v: reveal.bv, winner: reveal.bv > reveal.av, mine: reveal.pick === 1 } }
                : {})}
            />
          </div>
        )}

        <div className="mt-3 flex justify-center gap-1">
          {Array.from({ length: v.rounds }, (_, i) => {
            const h = v.history[i];
            return (
              <span
                key={i}
                className={`h-2.5 w-2.5 rounded-full ${h ? (h.right ? "bg-ok" : "bg-cold") : "bg-[rgba(128,128,128,0.3)]"}`}
              />
            );
          })}
        </div>

        {v.history.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-tghint">
            {[...v.history].reverse().map((h, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg bg-tgsection px-3 py-1.5">
                <span className="truncate">
                  {h.right ? "✅" : "❌"} {h.a.n} <b className="tabular-nums">{fmt(h.av)}</b> · {h.b.n}{" "}
                  <b className="tabular-nums">{fmt(h.bv)}</b>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Хайпожер! 📈" : "Не в тренді 📉"}
          subtitle={
            won
              ? `${v.right}/${v.rounds} · стрік ${s.me.streak} 🔥`
              : `${v.right}/${v.rounds} (треба ${v.winAt}) · стрік згорів`
          }
          scoreLabel="avg пром."
          formatScore={(n) => `${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
